import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  selectAmplitudeApiKey,
  selectGiaApiKeys,
  selectLanguage,
  selectStripeAccountId,
  selectStripeAccountName,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { getCurrentEnv } from 'helpers/getCurrentEnv'
import { getQueryParamsFromSearchString } from 'helpers/getQueryParamsFromSearchString'

import { TGiaApiKeys } from 'models/config.model'

import { Amplitude } from 'services/amplitude.service'
import { eventLogger } from 'services/eventLogger.service'
import { UserFlowTracker } from 'services/userFlowTracker.service'

import {
  CurrentEnvironment,
  EventLoggerInstanceName,
  TEST_ENV_QUERY_PARAM,
} from 'root-constants'

function getGIAAmplitudeOptions() {
  const btoaAuthCredentials = 'cHJveHktdXNlcjozQyk9Q3YncTd5L0A4Pjcp'

  return {
    apiEndpoint:
      getCurrentEnv() === CurrentEnvironment.PROD
        ? 'analytics.gismart.xyz/events'
        : 'analytics.gidev.xyz/events',
    headers: {
      Authorization: `Bearer ${btoaAuthCredentials}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    forceHttps: true,
  }
}

export function useEventLoggerInitialization(cohort = ''): void {
  const { search } = useLocation()
  const uuid = useSelector(selectUUID)
  const language = useSelector(selectLanguage)
  const amplitudeApiKey = useSelector(selectAmplitudeApiKey)
  const giaApiKeys = useSelector(selectGiaApiKeys) as TGiaApiKeys
  const stripeAccountId = useSelector(selectStripeAccountId)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const [giaInstance, setGiaInstance] = useState<Amplitude | null>(null)

  const URLParams = new URLSearchParams(search)
  const utmTagsFromUrl = getQueryParamsFromSearchString(search)
  const isTestEnvironment = URLParams.has(TEST_ENV_QUERY_PARAM)
  const isDevEnvironment = getCurrentEnv() !== CurrentEnvironment.PROD

  useEffect(() => {
    if (uuid && amplitudeApiKey && giaApiKeys) {
      const giaService = new Amplitude({
        apiKey: giaApiKeys?.[language],
        instanceName: EventLoggerInstanceName.GIA,
        options: getGIAAmplitudeOptions(),
        userId: uuid,
        utmTags: utmTagsFromUrl,
        isTestEnvironment,
        isDevEnvironment,
      })

      const userFlowTracker = new UserFlowTracker({
        instanceName: EventLoggerInstanceName.USER_FLOW_TRACKER,
        userId: uuid,
      })

      giaService.configure({ cohort })
      userFlowTracker.configure({ cohort })

      eventLogger.init(giaService, userFlowTracker)
      setGiaInstance(giaService)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, amplitudeApiKey, giaApiKeys])

  useEffect(() => {
    if (!stripeAccountId || !stripeAccountName) return

    giaInstance?.updateUserProperties(stripeAccountId, stripeAccountName)
  }, [giaInstance, stripeAccountId, stripeAccountName])
}
